import React from 'react'
import { Box, CircularProgress } from '@mui/material'
import MenuImportDetails from './MenuImportDetails'
import importMenu from '../mutations/importMenu'
import { useMenuImportReport } from '../queries/menuImportReport'
import { handleMutationError } from '../utils/errors'

const sxLoading = { display: 'flex', justifyContent: 'center', p: '3rem' } as const
const sxContent = { p: '1.5rem' } as const

// Der Import-Bericht im Dialog: erst auswerten, dann bestätigen — oder mit `importable` false nur
// ansehen. Der Dialog liegt außerhalb des ViewProviders, deshalb kommt das Restaurant als Prop.
const MenuImportDialogContent = ({
	restaurantId,
	source,
	importable,
	dismissPortal,
}: {
	restaurantId: string
	source: string
	importable: boolean
	dismissPortal: () => void
}): JSX.Element => {
	const { report, errors } = useMenuImportReport(restaurantId, source)
	const [importLoading, setImportLoading] = React.useState(false)

	// Lässt sich der Bericht nicht rechnen, etwa wegen einer unvollständigen KI-Bewertung, kommt der
	// Fehlerdialog, und dieser schließt.
	React.useEffect(() => {
		if (errors) {
			handleMutationError('menuImportReport', errors)
			dismissPortal()
		}
	}, [errors, dismissPortal])

	const handleImportMenu = React.useCallback(async () => {
		setImportLoading(true)
		const result = await importMenu({ restaurantId, source })
		setImportLoading(false)
		if (!result.success) {
			handleMutationError('importMenu', result.errors)
		} else {
			dismissPortal()
		}
	}, [restaurantId, source, dismissPortal])

	return report ? (
		<Box sx={sxContent}>
			<MenuImportDetails
				report={report}
				loading={importLoading}
				onCancel={dismissPortal}
				onConfirm={importable ? handleImportMenu : undefined}
				confirmLabel="Import bestätigen"
			/>
		</Box>
	) : (
		<Box sx={sxLoading}>
			<CircularProgress />
		</Box>
	)
}

export default React.memo(MenuImportDialogContent)
